import { PLAYWRIGHT_E2E_COOKIE } from '@lib/analytics/constants';
import {
  type ErrorTrackingExceptionEntry,
  isAllFirefoxInjectedExceptions,
  isAllResizeObserverLoopExceptions,
  isAllSyntheticCustomEventExceptions,
} from '@lib/analytics/synthetic-exception';
import { medusaSdkFingerprint } from '@lib/util/sentry-fingerprint';
import type * as Sentry from '@sentry/nextjs';

export const BROWSER_INJECTED_PREFIXES = [
  'app:///',
  'safari-extension://',
  'safari-web-extension://',
  'chrome-extension://',
  'moz-extension://',
];

export function isBrowserInjectedFilename(filename: string): boolean {
  return BROWSER_INJECTED_PREFIXES.some((prefix) =>
    filename.startsWith(prefix),
  );
}

type BeforeSend = NonNullable<
  NonNullable<Parameters<typeof Sentry.init>[0]>['beforeSend']
>;

/**
 * True when `values` is entirely one of the untraceable noise classes this
 * module drops: synthetic CustomEvent rejections, Firefox-for-iOS injected
 * userscript exceptions, or ResizeObserver loop notifications. Each predicate
 * is checked here (rather than via a message pattern in `ignoreErrors`) so a
 * chained/multi-exception event is only dropped when EVERY exception is the
 * same noise class — matching the PostHog `before_send` guard.
 * `ignoreErrors` drops the whole event if ANY exception message matches,
 * which would discard a real application error bundled alongside noise.
 */
function isUntraceableNoise(
  values: readonly ErrorTrackingExceptionEntry[] | undefined,
): boolean {
  return (
    isAllSyntheticCustomEventExceptions(values) ||
    isAllFirefoxInjectedExceptions(values) ||
    isAllResizeObserverLoopExceptions(values)
  );
}

/**
 * Returns a Sentry `beforeSend` callback for the client-side SDK.
 *
 * Extracted so the filtering logic can be unit-tested without a real browser.
 *
 * @param getCookies - returns the current `document.cookie` string
 * @param getHasConsent - returns true if analytics consent has been granted
 */
export function makeClientBeforeSend(
  getCookies: () => string,
  getHasConsent: () => boolean,
): BeforeSend {
  const e2eCookieRe = new RegExp(`(?:^|;\\s*)${PLAYWRIGHT_E2E_COOKIE}=`);
  return (event, hint) => {
    if (e2eCookieRe.test(getCookies())) {
      return null;
    }

    if (isUntraceableNoise(event.exception?.values)) {
      return null;
    }

    const frames = event.exception?.values?.flatMap(
      (v) => v.stacktrace?.frames ?? [],
    );
    if (frames?.some((f) => isBrowserInjectedFilename(f.filename ?? ''))) {
      return null;
    }

    if (!getHasConsent()) {
      delete event.user;

      if (event.request) {
        delete event.request.cookies;
        delete event.request.headers;
      }
    }

    const fingerprint = medusaSdkFingerprint(hint?.originalException);
    if (fingerprint) {
      event.fingerprint = fingerprint;
    }

    return event;
  };
}
